import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment";
import { Button, Input, Modal, Select, Table, Typography } from "antd";
import { SaveOutlined, SwapOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { saveEntryBox, searchCarpets, searchEntries, setEntryCleaned, entryCounts } from "../../redux/actions";
import CleanStatus from "../CarpetPreview/CleanStatus";
import dateColumn from "../CarpetPreview/dateColumn";
import ShopStatus from "../CarpetPreview/ShopStatus";
import ClientSelect from "./ClientSelect";
import { clientLabel, dimensionsText, money } from "./format";

const { Title } = Typography;
const nowrap = {
  onCell: () => ({ style: { whiteSpace: "nowrap" } }),
  onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
};

export default function Carpets({ fixedClientId }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [clientId, setClientId] = useState(fixedClientId);
  const [status, setStatus] = useState("all");
  const [ref, setRef] = useState("");
  const [category, setCategory] = useState(undefined);
  const [box, setBox] = useState("");
  const [counts, setCounts] = useState({ total: 0, inShop: 0, out: 0 });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [historyCarpet, setHistoryCarpet] = useState(null);
  const [history, setHistory] = useState({ data: [], total: 0 });
  const [historyPage, setHistoryPage] = useState(1);

  const filters = {
    client: fixedClientId || clientId,
    status,
    ref,
    category,
    storageBox: box,
  };

  const load = (nextPage = page, nextSize = pageSize) => {
    setLoading(true);
    dispatch(entryCounts(filters)).then(({ payload }) => setCounts(payload || { total: 0, inShop: 0, out: 0 }));
    dispatch(searchCarpets(nextPage, nextSize, filters))
      .then(({ payload }) => {
        setRows(payload.data || []);
        setTotal(payload.total || 0);
        setPage(nextPage);
        setPageSize(nextSize);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const handle = setTimeout(() => load(1, pageSize), 300);
    return () => clearTimeout(handle);
  }, [clientId, status, ref, category, box, fixedClientId]);

  const loadHistory = (carpet, nextPage = 1) => {
    setHistoryCarpet(carpet);
    dispatch(searchEntries(nextPage, 10, { carpet: carpet._id }))
      .then(({ payload }) => {
        setHistory(payload);
        setHistoryPage(nextPage);
      });
  };

  const saveBox = (row) => {
    const storageBox = drafts[row.entryId] != null ? drafts[row.entryId] : row.storageBox;
    dispatch(saveEntryBox(row.entryId, storageBox)).then(() => {
      setDrafts((current) => {
        const next = { ...current };
        delete next[row.entryId];
        return next;
      });
      load();
    });
  };

  return (
    <div>
      {!fixedClientId && <Title level={4} style={{ marginTop: 0 }}>{intl.formatMessage({ id: "carpets" })}</Title>}
      <div style={{ marginBottom: 12 }}>
        <strong>{intl.formatMessage({ id: "total" })}: {counts.total}</strong>
        <span style={{ marginLeft: 16 }}>{intl.formatMessage({ id: "imported" })}: {counts.inShop}</span>
        <span style={{ marginLeft: 16 }}>{intl.formatMessage({ id: "exported" })}: {counts.out}</span>
      </div>
      <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {!fixedClientId && (
          <ClientSelect value={clientId} onChange={setClientId} style={{ width: 220 }} />
        )}
        <Select value={status} style={{ width: 140 }} onChange={setStatus}>
          <Select.Option value="all">{intl.formatMessage({ id: "all" })}</Select.Option>
          <Select.Option value="in">{intl.formatMessage({ id: "inShop" })}</Select.Option>
          <Select.Option value="out">{intl.formatMessage({ id: "checkedOut" })}</Select.Option>
        </Select>
        <Input placeholder={intl.formatMessage({ id: "ref" })} value={ref} style={{ width: 100 }} onChange={(event) => setRef(event.target.value)} />
        <CategorySelect value={category} onChange={setCategory} />
        <Input placeholder={intl.formatMessage({ id: "storageBox" })} value={box} style={{ width: 120 }} onChange={(event) => setBox(event.target.value)} />
      </div>
      <Table
        size="small"
        bordered
        rowKey="_id"
        loading={loading}
        dataSource={rows}
        pagination={{ current: page, pageSize, total, showSizeChanger: true }}
        onChange={(pagination) => load(pagination.current, pagination.pageSize)}
        columns={[
          { title: intl.formatMessage({ id: "ref" }), dataIndex: "_ref", width: 70 },
          { title: intl.formatMessage({ id: "client" }), render: (row) => clientLabel(row.client) },
          { title: intl.formatMessage({ id: "category" }), render: (row) => row.category ? intl.formatMessage({ id: row.category }) : "" },
          { title: intl.formatMessage({ id: "widthXLength" }), render: (row) => dimensionsText(row) },
          { title: intl.formatMessage({ id: "description" }), dataIndex: "description" },
          {
            title: intl.formatMessage({ id: "price" }),
            width: 150,
            onHeaderCell: () => ({ style: { textAlign: "center" } }),
            render: (row) => (
              <div style={{ display: "flex", margin: "-8px" }}>
                <div style={{ flex: 1, padding: "8px", borderRight: "1px solid #f0f0f0", textDecoration: row.jobPrice != null && row.jobPrice !== "" ? "line-through" : undefined }}>{money(row.price)}</div>
                <div style={{ flex: 1, padding: "8px" }}>{money(row.jobPrice)}</div>
              </div>
            ),
          },
          { title: intl.formatMessage({ id: "status" }), width: 90, render: (row) => <ShopStatus inShop={row.inShop} /> },
          {
            title: intl.formatMessage({ id: "cleanStatus" }),
            width: 150,
            render: (row) => {
              if (row.cleaned == null && !row.entryId) return "";
              const action = intl.formatMessage({ id: "cleanStatus" });
              return (
                <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <CleanStatus cleaned={!!row.cleaned} />
                  {row.inShop && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<SwapOutlined />}
                      aria-label={action}
                      title={action}
                      onClick={() => dispatch(setEntryCleaned(row.entryId, !row.cleaned)).then(() => load())}
                    />
                  )}
                </span>
              );
            },
          },
          dateColumn(intl.formatMessage({ id: "date" }), (row) => (row.date ? moment(row.date).format("DD/MM/YYYY HH:mm") : "")),
          {
            title: intl.formatMessage({ id: "storageBox" }),
            width: 160,
            render: (row) => {
              if (!row.inShop) return "";
              const value = drafts[row.entryId] != null ? drafts[row.entryId] : (row.storageBox || "");
              const changed = drafts[row.entryId] != null && drafts[row.entryId] !== (row.storageBox || "");
              const saveLabel = intl.formatMessage({ id: "save" });
              return (
                <span style={{ display: "flex", gap: 4 }}>
                  <Input
                    size="small"
                    value={value}
                    onChange={(event) => {
                      const next = event.target.value;
                      setDrafts((current) => ({ ...current, [row.entryId]: next }));
                    }}
                  />
                  <Button
                    size="small"
                    type="primary"
                    icon={<SaveOutlined />}
                    aria-label={saveLabel}
                    title={saveLabel}
                    disabled={!changed}
                    onClick={() => saveBox(row)}
                  />
                </span>
              );
            },
          },
          {
            width: 48,
            render: (row) => (
              <Button
                size="small"
                type="primary"
                icon={<UnorderedListOutlined />}
                aria-label={intl.formatMessage({ id: "entries" })}
                title={intl.formatMessage({ id: "entries" })}
                onClick={() => loadHistory(row, 1)}
              />
            ),
          },
        ]}
      />
      <Modal
        title={historyCarpet ? `${intl.formatMessage({ id: "entries" })} #${historyCarpet._ref}` : ""}
        visible={!!historyCarpet}
        width="95vw"
        footer={null}
        onCancel={() => setHistoryCarpet(null)}
        destroyOnClose
      >
        <Table
          size="small"
          bordered
          rowKey="_id"
          dataSource={history.data}
          pagination={{ current: historyPage, pageSize: 10, total: history.total }}
          onChange={(pagination) => loadHistory(historyCarpet, pagination.current)}
          scroll={{ x: "max-content" }}
          columns={[
            dateColumn(intl.formatMessage({ id: "inAt" }), (entry) => moment(entry.inAt).format("DD/MM/YYYY HH:mm")),
            dateColumn(intl.formatMessage({ id: "outAt" }), (entry) => (entry.outAt ? moment(entry.outAt).format("DD/MM/YYYY HH:mm") : "")),
            { title: intl.formatMessage({ id: "cleanStatus" }), width: 120, ...nowrap, render: (entry) => <CleanStatus cleaned={entry.cleaned} /> },
            {
              title: intl.formatMessage({ id: "price" }),
              width: 150,
              onCell: () => ({ style: { whiteSpace: "nowrap" } }),
              onHeaderCell: () => ({ style: { whiteSpace: "nowrap", textAlign: "center" } }),
              render: (entry) => (
                <div style={{ display: "flex", margin: "-8px" }}>
                  <div style={{ flex: 1, padding: "8px", borderRight: "1px solid #f0f0f0", textDecoration: entry.jobPrice != null && entry.jobPrice !== "" ? "line-through" : undefined }}>{money((entry.carpet || {}).price)}</div>
                  <div style={{ flex: 1, padding: "8px" }}>{money(entry.jobPrice)}</div>
                </div>
              ),
            },
            { title: intl.formatMessage({ id: "storageBox" }), dataIndex: "storageBox", ...nowrap },
            { title: intl.formatMessage({ id: "inNotes" }), dataIndex: "inNotes", ...nowrap },
            { title: intl.formatMessage({ id: "outNotes" }), dataIndex: "outNotes", ...nowrap },
          ]}
        />
      </Modal>
    </div>
  );
}

function CategorySelect({ value, onChange }) {
  const intl = useIntl();
  const config = useSelector((state) => state.config, shallowEqual);
  const categories = (config && config.carpetCategories) || [];
  return (
    <Select
      allowClear
      placeholder={intl.formatMessage({ id: "category" })}
      value={value}
      style={{ width: 140 }}
      onChange={onChange}
    >
      {categories.map((item) => (
        <Select.Option key={item} value={item}>{intl.formatMessage({ id: item })}</Select.Option>
      ))}
    </Select>
  );
}
